import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { positionsApi } from '@/lib/api/client';
import { chainKeys } from './useChains';
import type { ClosePositionRequest, RollPositionRequest } from '@steward/shared';
import toast from 'react-hot-toast';

export const positionKeys = {
  all: ['positions'] as const,
  details: () => [...positionKeys.all, 'detail'] as const,
  detail: (id: string) => [...positionKeys.details(), id] as const,
};

export function usePosition(id: string) {
  return useQuery({
    queryKey: positionKeys.detail(id),
    queryFn: () => positionsApi.get(id),
    select: (data) => data.position,
    enabled: !!id,
  });
}

export function useClosePosition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ClosePositionRequest }) =>
      positionsApi.close(id, data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: positionKeys.all });
      queryClient.invalidateQueries({ queryKey: chainKeys.all });
      toast.success('Position closed');
      return result;
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to close position');
    },
  });
}

export function useRollPosition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: RollPositionRequest }) =>
      positionsApi.roll(id, data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: positionKeys.all });
      queryClient.invalidateQueries({ queryKey: chainKeys.all });
      toast.success('Position rolled');
      return result;
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to roll position');
    },
  });
}
